import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Platform, ActivityIndicator, Modal } from 'react-native';
import { Picker } from '@react-native-picker/picker';
import WebScrollView from '../components/WebScrollView';
import { Plus, Search, Tag, Package, Calendar, DollarSign, X, Upload, Barcode, Info, Edit } from '../lib/icons';
import { supabase } from '../lib/supabase';
import { openFilePicker } from '../lib/filePicker';
import ItemDetailModal from '../components/inventory/ItemDetailModal';
import { formatDateForDisplay } from '../lib/dateUtils';
import { useActiveHome } from '../contexts/ActiveHomeContext';

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface InventoryItem {
  id: string;
  home_id: string;
  name: string;
  category: string;
  purchase_date: string;
  purchase_price: number;
  model_number: string;
  serial_number: string;
  barcode: string;
  notes: string;
  images: string[];
}

interface Home {
  id: string;
  address: string;
  city: string;
  state: string;
  cover_photo_url?: string;
  lot_size?: number;
  bedrooms?: number;
  bathrooms?: number;
}

const categories = [
  'Appliances',
  'Electronics',
  'Furniture',
  'HVAC',
  'Plumbing',
  'Tools',
  'Outdoor',
  'Other'
];

// Custom Dropdown Picker Component
const DropdownPicker = ({ 
  visible, 
  onClose, 
  options, 
  selectedValue, 
  onSelect 
}: { 
  visible: boolean; 
  onClose: () => void; 
  options: Array<{ value: string; label: string }>; 
  selectedValue: string; 
  onSelect: (value: string) => void;
}) => {
  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={pickerStyles.pickerModalOverlay}>
        <Pressable style={pickerStyles.pickerModalBackdrop} onPress={onClose} />
        <View style={pickerStyles.pickerModalContent}>
          <View style={pickerStyles.pickerHeader}>
            <Text style={pickerStyles.pickerHeaderText}>Select Option</Text>
            <Pressable onPress={onClose}>
              <X size={20} color="#6b7280" />
            </Pressable>
          </View>
          <WebScrollView style={pickerStyles.pickerOptions}>
            {options.map((option) => (
              <Pressable
                key={option.value}
                onPress={() => {
                  onSelect(option.value);
                  onClose();
                }}
                style={[
                  pickerStyles.pickerOption,
                  selectedValue === option.value && pickerStyles.pickerOptionSelected
                ]}
              >
                <Text style={[
                  pickerStyles.pickerOptionText,
                  selectedValue === option.value && pickerStyles.pickerOptionTextSelected
                ]}>
                  {option.label}
                </Text>
              </Pressable>
            ))}
          </WebScrollView>
        </View>
      </View>
    </Modal>
  );
};

const Inventory = () => {
  const { homes: accessibleHomes, activeHome, setActiveHomeById, loading: homesLoading } = useActiveHome();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [homes, setHomes] = useState<Home[]>([]);
  const [selectedHome, setSelectedHome] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showHomePicker, setShowHomePicker] = useState(false);
  const [formData, setFormData] = useState<Partial<InventoryItem>>({
    name: '',
    category: categories[0],
    purchase_date: getTodayDate(),
    purchase_price: 0,
    model_number: '',
    serial_number: '',
    barcode: '',
    notes: '',
    images: []
  });
  const [editFormData, setEditFormData] = useState<Partial<InventoryItem>>({
    name: '',
    category: categories[0],
    purchase_date: '',
    purchase_price: 0,
    model_number: '',
    serial_number: '',
    barcode: '',
    notes: '',
    images: []
  });

  useEffect(() => {
    setHomes(accessibleHomes as Home[]);
    if (activeHome?.id) {
      setSelectedHome(activeHome.id);
    } else if (!homesLoading) {
      setSelectedHome('');
      setLoading(false);
    }
  }, [accessibleHomes, activeHome, homesLoading]);

  useEffect(() => {
    if (selectedHome) {
      fetchItems();
    }
  }, [selectedHome]);

  const fetchItems = async () => {
    try {
      const { data, error } = await supabase
        .from('inventory_items')
        .select('*')
        .eq('home_id', selectedHome)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error('Error fetching inventory items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const { error } = await supabase
        .from('inventory_items')
        .insert([{
          ...formData,
          home_id: selectedHome,
          purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price.toString()) : null
        }]);

      if (error) throw error;

      setFormData({
        name: '',
        category: categories[0],
        purchase_date: getTodayDate(),
        purchase_price: 0,
        model_number: '',
        serial_number: '',
        barcode: '',
        notes: '',
        images: []
      });
      setIsFormOpen(false);
      fetchItems();
    } catch (error: any) {
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!editingItem) return;

    setEditSubmitting(true);
    setError('');

    try {
      const { error } = await supabase
        .from('inventory_items')
        .update({
          name: editFormData.name,
          category: editFormData.category,
          purchase_date: editFormData.purchase_date || null,
          purchase_price: editFormData.purchase_price ? parseFloat(editFormData.purchase_price.toString()) : null,
          model_number: editFormData.model_number || null,
          serial_number: editFormData.serial_number || null,
          barcode: editFormData.barcode || null,
          notes: editFormData.notes || null,
          images: editFormData.images || []
        })
        .eq('id', editingItem.id);

      if (error) throw error;

      setIsEditFormOpen(false);
      setEditingItem(null);
      fetchItems();
    } catch (error: any) {
      setError(error.message);
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleImageUploadFromInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    await handleImageUpload(Array.from(files));
  };

  const handleImageUpload = async (files: File[]) => {
    if (!files || files.length === 0) return;

    setUploadError(null);
    const newProgress = {...uploadProgress};
    
    try {
      const uploadPromises = Array.from(files).map(async (file, index) => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const filePath = `images/${fileName}`;

        const progressKey = `${fileName}-${index}`;
        newProgress[progressKey] = 0;
        setUploadProgress(newProgress);

        const { error: uploadError } = await supabase.storage
          .from('inventory')
          .upload(filePath, file);
        
        // Simulate progress for UI feedback
        setUploadProgress(prev => ({
          ...prev,
          [progressKey]: 100
        }));

        if (uploadError) {
          if (uploadError.message.includes('Bucket not found')) {
            throw new Error('Storage not configured. Please contact support.');
          }
          throw uploadError;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('inventory')
          .getPublicUrl(filePath);

        setUploadProgress(prev => {
          const updated = {...prev};
          delete updated[progressKey];
          return updated;
        });

        return publicUrl;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      setFormData(prev => ({
        ...prev,
        images: [...(prev.images || []), ...uploadedUrls]
      }));
    } catch (error: any) {
      console.error('Error uploading images:', error);
      setUploadError(error.message);
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.model_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.barcode?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <WebScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>Home Inventory</Text>
          <Text style={styles.subtitle}>Track and manage your home items</Text>
        </View>
        <Pressable
          onPress={() => setIsFormOpen(true)}
          style={styles.addButton}
        >
          <Plus size={16} color="#ffffff" />
          <Text style={styles.addButtonText}>Add Item</Text>
        </Pressable>
      </View>

      <View style={styles.card}>
        <View style={styles.cardContent}>
          <View style={styles.filtersContainer}>
            {homes.length > 1 && (
              <Pressable
                onPress={() => setShowHomePicker(true)}
                style={styles.homeSelector}
              >
                <Text style={styles.homeSelectorText} numberOfLines={1}>
                  {homes.find(h => h.id === selectedHome)?.address || 'Select Home'}
                </Text>
              </Pressable>
            )}
            <View style={styles.searchContainer}>
              <View style={styles.searchIconContainer}>
                <Search size={16} color="#9ca3af" />
              </View>
              <TextInput
                value={searchTerm}
                onChangeText={setSearchTerm}
                placeholder="Search items..."
                style={styles.searchInput}
              />
            </View>
          </View>

          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#9ca3af" />
            </View>
          ) : filteredItems.length === 0 ? (
            <View style={styles.emptyStateContainer}>
              <Package size={48} color="#9ca3af" />
              <Text style={styles.emptyStateTitle}>No Items Found</Text>
              <Text style={styles.emptyStateDescription}>
                {items.length === 0
                  ? "Start adding items to your inventory"
                  : "No items match your search criteria"}
              </Text>
            </View>
          ) : (
            <View style={styles.itemsGrid}>
              {filteredItems.map((item) => (
                <View key={item.id} style={styles.itemCard}>
                  <View style={styles.itemCardHeader}>
                    <Pressable 
                      style={styles.itemCardHeaderLeft}
                      onPress={() => setSelectedItemId(item.id)}
                    >
                      <Text style={styles.itemCardTitle}>{item.name}</Text>
                      <View style={styles.categoryBadge}>
                        <Tag size={16} color="#6b7280" />
                        <Text style={styles.categoryBadgeText}>{item.category}</Text>
                      </View>
                    </Pressable>
                    <View style={styles.itemCardActions}>
                      <Pressable
                        onPress={(e) => {
                          setEditingItem(item);
                          setEditFormData({
                            name: item.name,
                            category: item.category,
                            purchase_date: item.purchase_date,
                            purchase_price: item.purchase_price,
                            model_number: item.model_number,
                            serial_number: item.serial_number,
                            barcode: item.barcode,
                            notes: item.notes,
                            images: item.images
                          });
                          setIsEditFormOpen(true);
                        }}
                        style={styles.editButton}
                      >
                        <Edit size={16} color="#6b7280" />
                      </Pressable>
                      {item.images && item.images.length > 0 && Platform.OS === 'web' && (
                        <img
                          src={item.images[0]}
                          alt={item.name}
                          style={{
                            width: 64,
                            height: 64,
                            objectFit: 'cover',
                            borderRadius: 8,
                            cursor: 'pointer'
                          }}
                          onClick={() => setSelectedItemId(item.id)}
                        />
                      )}
                    </View>
                  </View>

                  <Pressable 
                    style={styles.itemCardDetails}
                    onPress={() => setSelectedItemId(item.id)}
                  >
                    {item.purchase_date && (
                      <View style={styles.itemDetail}>
                        <Calendar size={16} color="#6b7280" />
                        <Text style={styles.itemDetailText}>
                          Purchased: {formatDateForDisplay(item.purchase_date)}
                        </Text>
                      </View>
                    )}
                    {item.purchase_price && (
                      <View style={styles.itemDetail}>
                        <DollarSign size={16} color="#6b7280" />
                        <Text style={styles.itemDetailText}>{formatCurrency(item.purchase_price)}</Text>
                      </View>
                    )}
                    {item.model_number && (
                      <View style={styles.itemDetail}>
                        <Info size={16} color="#6b7280" />
                        <Text style={styles.itemDetailText}>Model: {item.model_number}</Text>
                      </View>
                    )}
                  </Pressable>
                </View>
              ))}
            </View>
          )}
        </View>
      </View>

      {/* Add Item Modal */}
      {isFormOpen && (
        <Modal
          visible={isFormOpen}
          transparent
          animationType="slide"
          onRequestClose={() => setIsFormOpen(false)}
        >
          <View style={styles.modalOverlay}>
            <Pressable style={styles.modalBackdrop} onPress={() => setIsFormOpen(false)} />
            <View style={styles.modalSheet}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Add Inventory Item</Text>
                <Pressable onPress={() => setIsFormOpen(false)}>
                  <X size={20} color="#9ca3af" />
                </Pressable>
              </View>

              <WebScrollView style={styles.modalBody}>
                <View style={styles.form}>
                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Item Name</Text>
                    <TextInput
                  value={formData.name}
                      onChangeText={(value) => setFormData({ ...formData, name: value })}
                  placeholder="Enter item name"
                      style={styles.input}
                    />
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Category</Text>
                    {Platform.OS === 'web' ? (
                      <select
                        value={formData.category || categories[0]}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        style={{
                          padding: '8px 12px',
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          borderColor: '#d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          color: '#111827',
                          backgroundColor: '#ffffff',
                          width: '100%',
                          fontFamily: 'inherit',
                          outlineStyle: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        {categories.map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <View style={styles.pickerContainer}>
                        <Picker
                          selectedValue={formData.category || categories[0]}
                          onValueChange={(value) => setFormData({ ...formData, category: value })}
                          style={styles.picker}
                        >
                          {categories.map((category) => (
                            <Picker.Item key={category} label={category} value={category} />
                          ))}
                        </Picker>
                      </View>
                    )}
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Purchase Date</Text>
                    {Platform.OS === 'web' ? (
                      <input
                        type="date"
                        value={formData.purchase_date || ''}
                        onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                        style={{
                          padding: '8px 12px',
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          borderColor: '#d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          color: '#111827',
                          backgroundColor: '#ffffff',
                          width: '100%',
                          fontFamily: 'inherit',
                          outlineStyle: 'none',
                        }}
                      />
                    ) : (
                      <TextInput
                        value={formData.purchase_date}
                        onChangeText={(value) => setFormData({ ...formData, purchase_date: value })}
                        placeholder="YYYY-MM-DD"
                        style={styles.input}
                      />
                    )}
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Purchase Price</Text>
                    <View style={styles.inputWithIcon}>
                      <View style={styles.inputIconLeft}>
                        <DollarSign size={16} color="#9ca3af" />
                      </View>
                      <TextInput
                        value={formData.purchase_price === null || formData.purchase_price === 0 ? '' : formData.purchase_price.toString()}
                        onChangeText={(value) => setFormData({ 
                      ...formData, 
                          purchase_price: value ? parseFloat(value) : 0
                    })}
                    placeholder="0.00"
                        keyboardType="decimal-pad"
                        style={[styles.input, styles.inputWithLeftIcon]}
                      />
                    </View>
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Model Number</Text>
                    <TextInput
                  value={formData.model_number}
                      onChangeText={(value) => setFormData({ ...formData, model_number: value })}
                  placeholder="Optional model number"
                      style={styles.input}
                    />
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Serial Number</Text>
                    <TextInput
                  value={formData.serial_number}
                      onChangeText={(value) => setFormData({ ...formData, serial_number: value })}
                  placeholder="Optional serial number"
                      style={styles.input}
                    />
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Barcode</Text>
                    <View style={styles.inputWithIcon}>
                      <View style={styles.inputIconLeft}>
                        <Barcode size={16} color="#9ca3af" />
                      </View>
                      <TextInput
                    value={formData.barcode}
                        onChangeText={(value) => setFormData({ ...formData, barcode: value })}
                    placeholder="Optional barcode"
                        style={[styles.input, styles.inputWithLeftIcon]}
                      />
                    </View>
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Notes</Text>
                    <TextInput
                  value={formData.notes}
                      onChangeText={(value) => setFormData({ ...formData, notes: value })}
                  placeholder="Optional notes about the item"
                      multiline
                      numberOfLines={3}
                      style={[styles.input, styles.textArea]}
                    />
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Images</Text>
                    <View style={styles.uploadContainer}>
                  {Object.keys(uploadProgress).length > 0 ? (
                        <View style={styles.uploadingContainer}>
                          <ActivityIndicator size="large" color="#9ca3af" />
                          <Text style={styles.uploadingText}>Uploading images...</Text>
                      {Object.entries(uploadProgress).map(([key, progress]) => (
                            <View key={key} style={styles.progressBarContainer}>
                              <View style={[styles.progressBar, { width: `${progress}%` }]} />
                            </View>
                          ))}
                        </View>
                      ) : (
                        <View style={styles.uploadPrompt}>
                          <Upload size={48} color="#9ca3af" />
                          {Platform.OS === 'web' ? (
                            <label style={{ cursor: 'pointer', marginTop: 16 }}>
                              <Text style={styles.uploadText}>Upload images</Text>
                              <input
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={handleImageUploadFromInput}
                                style={{ display: 'none' }}
                              />
                            </label>
                          ) : (
                            <Pressable
                              onPress={async () => {
                                try {
                                  const files = await openFilePicker();
                                  if (files.length > 0) {
                                    await handleImageUpload(files);
                                  }
                                } catch (error: any) {
                                  console.error('Error picking images:', error);
                                  setUploadError(error.message || 'Failed to pick images');
                                }
                              }}
                            >
                              <Text style={styles.uploadText}>Upload images</Text>
                            </Pressable>
                          )}
                          <Text style={styles.uploadHint}>PNG, JPG up to 10MB</Text>
                        </View>
                  )}
                  
                  {uploadError && (
                        <View style={styles.uploadError}>
                          <Text style={styles.uploadErrorText}>{uploadError}</Text>
                        </View>
                  )}

                  {formData.images && formData.images.length > 0 && (
                        <View style={styles.uploadedImagesContainer}>
                          <Text style={styles.uploadedImagesTitle}>Uploaded Images</Text>
                          <View style={styles.uploadedImagesGrid}>
                          {formData.images.map((url, index) => (
                              <View key={index} style={styles.uploadedImageWrapper}>
                                {Platform.OS === 'web' && (
                              <img
                                src={url}
                                alt={`Uploaded image ${index + 1}`}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      borderRadius: 8
                                    }}
                                  />
                                )}
                                <Pressable
                                  onPress={() => setFormData(prev => ({
                                  ...prev,
                                  images: prev.images?.filter((_, i) => i !== index) || []
                                }))}
                                  style={styles.removeImageButton}
                                >
                                  <X size={16} color="#ffffff" />
                                </Pressable>
                              </View>
                            ))}
                          </View>
                        </View>
                      )}
                    </View>
                  </View>

              {error && (
                    <Text style={styles.errorText}>{error}</Text>
                  )}
                </View>
              </WebScrollView>

              <View style={styles.modalFooter}>
                <Pressable onPress={() => setIsFormOpen(false)} style={styles.cancelButton}>
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </Pressable>
                <Pressable
                  onPress={handleSubmit}
                  disabled={submitting}
                  style={[styles.submitButton, submitting && styles.submitButtonDisabled]}
                >
                  {submitting ? (
                    <>
                      <ActivityIndicator size="small" color="#ffffff" />
                      <Text style={styles.submitButtonText}>Adding...</Text>
                    </>
                  ) : (
                    <Text style={styles.submitButtonText}>Add Item</Text>
                  )}
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>
      )}

      {/* Edit Item Modal */}
      {isEditFormOpen && editingItem && (
        <Modal
          visible={isEditFormOpen}
          transparent
          animationType="slide"
          onRequestClose={() => {
                    setIsEditFormOpen(false);
                    setEditingItem(null);
                  }}
        >
          <View style={styles.modalOverlay}>
            <Pressable style={styles.modalBackdrop} onPress={() => {
              setIsEditFormOpen(false);
              setEditingItem(null);
            }} />
            <View style={styles.modalSheet}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Edit Inventory Item</Text>
                <Pressable onPress={() => {
                  setIsEditFormOpen(false);
                  setEditingItem(null);
                }}>
                  <X size={20} color="#9ca3af" />
                </Pressable>
              </View>

              <WebScrollView style={styles.modalBody}>
                <View style={styles.form}>
                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Item Name</Text>
                    <TextInput
                    value={editFormData.name}
                      onChangeText={(value) => setEditFormData({ ...editFormData, name: value })}
                    placeholder="Enter item name"
                      style={styles.input}
                    />
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Category</Text>
                    {Platform.OS === 'web' ? (
                      <select
                        value={editFormData.category || categories[0]}
                        onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                        style={{
                          padding: '8px 12px',
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          borderColor: '#d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          color: '#111827',
                          backgroundColor: '#ffffff',
                          width: '100%',
                          fontFamily: 'inherit',
                          outlineStyle: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        {categories.map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <View style={styles.pickerContainer}>
                        <Picker
                          selectedValue={editFormData.category || categories[0]}
                          onValueChange={(value) => setEditFormData({ ...editFormData, category: value })}
                          style={styles.picker}
                        >
                          {categories.map((category) => (
                            <Picker.Item key={category} label={category} value={category} />
                          ))}
                        </Picker>
                      </View>
                    )}
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Purchase Date</Text>
                    {Platform.OS === 'web' ? (
                      <input
                        type="date"
                        value={editFormData.purchase_date || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, purchase_date: e.target.value })}
                        style={{
                          padding: '8px 12px',
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          borderColor: '#d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          color: '#111827',
                          backgroundColor: '#ffffff',
                          width: '100%',
                          fontFamily: 'inherit',
                          outlineStyle: 'none',
                        }}
                      />
                    ) : (
                      <TextInput
                        value={editFormData.purchase_date}
                        onChangeText={(value) => setEditFormData({ ...editFormData, purchase_date: value })}
                        placeholder="YYYY-MM-DD"
                        style={styles.input}
                      />
                    )}
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Purchase Price</Text>
                    <View style={styles.inputWithIcon}>
                      <View style={styles.inputIconLeft}>
                        <DollarSign size={16} color="#9ca3af" />
                      </View>
                      <TextInput
                        value={editFormData.purchase_price === null || editFormData.purchase_price === 0 ? '' : editFormData.purchase_price.toString()}
                        onChangeText={(value) => setEditFormData({ 
                        ...editFormData, 
                          purchase_price: value ? parseFloat(value) : 0
                      })}
                      placeholder="0.00"
                        keyboardType="decimal-pad"
                        style={[styles.input, styles.inputWithLeftIcon]}
                      />
                    </View>
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Model Number</Text>
                    <TextInput
                    value={editFormData.model_number}
                      onChangeText={(value) => setEditFormData({ ...editFormData, model_number: value })}
                    placeholder="Optional model number"
                      style={styles.input}
                    />
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Serial Number</Text>
                    <TextInput
                    value={editFormData.serial_number}
                      onChangeText={(value) => setEditFormData({ ...editFormData, serial_number: value })}
                    placeholder="Optional serial number"
                      style={styles.input}
                    />
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Barcode</Text>
                    <View style={styles.inputWithIcon}>
                      <View style={styles.inputIconLeft}>
                        <Barcode size={16} color="#9ca3af" />
                      </View>
                      <TextInput
                      value={editFormData.barcode}
                        onChangeText={(value) => setEditFormData({ ...editFormData, barcode: value })}
                      placeholder="Optional barcode"
                        style={[styles.input, styles.inputWithLeftIcon]}
                      />
                    </View>
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Notes</Text>
                    <TextInput
                    value={editFormData.notes}
                      onChangeText={(value) => setEditFormData({ ...editFormData, notes: value })}
                    placeholder="Optional notes about the item"
                      multiline
                      numberOfLines={3}
                      style={[styles.input, styles.textArea]}
                  />
                  </View>

                {error && (
                    <Text style={styles.errorText}>{error}</Text>
                  )}
                </View>
              </WebScrollView>

              <View style={styles.modalFooter}>
                <Pressable onPress={() => {
                    setIsEditFormOpen(false);
                    setEditingItem(null);
                }} style={styles.cancelButton}>
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </Pressable>
                <Pressable
                  onPress={handleEditSubmit}
                  disabled={editSubmitting}
                  style={[styles.submitButton, editSubmitting && styles.submitButtonDisabled]}
                >
                  {editSubmitting ? (
                    <>
                      <ActivityIndicator size="small" color="#ffffff" />
                      <Text style={styles.submitButtonText}>Saving...</Text>
                    </>
                  ) : (
                    <Text style={styles.submitButtonText}>Save Changes</Text>
                  )}
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>
      )}

      {selectedItemId && (
        <ItemDetailModal
          itemId={selectedItemId}
          onClose={() => setSelectedItemId(null)}
          onUpdate={fetchItems}
        />
      )}

      <DropdownPicker
        visible={showHomePicker}
        onClose={() => setShowHomePicker(false)}
        options={homes.map(home => ({ 
          value: home.id, 
          label: `${home.address}, ${home.city}` 
        }))}
        selectedValue={selectedHome}
                    onSelect={(homeId) => {
                      setSelectedHome(homeId);
                      setActiveHomeById(homeId);
                    }}
      />

    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  contentContainer: {
    padding: 16,
    gap: 32,
  },
  header: {
    gap: 24,
  },
  title: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#111827',
  },
  subtitle: {
    fontSize: 16,
    color: '#6b7280',
    marginTop: 4,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  addButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    ...Platform.select({
      web: {
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 2,
        elevation: 1,
      },
    }),
  },
  cardContent: {
    padding: 24,
    gap: 24,
  },
  filtersContainer: {
    gap: 16,
  },
  homeSelector: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  homeSelectorText: {
    fontSize: 14,
    color: '#111827',
  },
  searchContainer: {
    position: 'relative',
    maxWidth: 448,
  },
  searchIconContainer: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: [{ translateY: -8 }],
    zIndex: 1,
  },
  searchInput: {
    paddingLeft: 36,
    paddingRight: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 14,
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none',
    }),
  },
  loadingContainer: {
    padding: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStateContainer: {
    padding: 48,
    alignItems: 'center',
    gap: 16,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: '500',
    color: '#111827',
  },
  emptyStateDescription: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
  },
  itemsGrid: {
    gap: 24,
    ...(Platform.OS === 'web' && {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    }),
  },
  itemCard: {
    padding: 24,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    gap: 16,
    ...(Platform.OS === 'web' && {
      transition: 'background-color 0.2s',
    }),
  },
  itemCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
  },
  itemCardHeaderLeft: {
    flex: 1,
    gap: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  itemCardTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  categoryBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  categoryBadgeText: {
    fontSize: 14,
    color: '#6b7280',
  },
  itemCardActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  editButton: {
    padding: 8,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 2,
        elevation: 1,
      },
    }),
  },
  itemCardDetails: {
    gap: 8,
  },
  itemDetail: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  itemDetailText: {
    fontSize: 14,
    color: '#6b7280',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  modalBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  modalSheet: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxWidth: 672,
    width: '100%',
    maxHeight: '90%',
    ...Platform.select({
      web: {
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 20 },
        shadowOpacity: 0.1,
        shadowRadius: 25,
        elevation: 10,
      },
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  modalBody: {
    flex: 1,
  },
  form: {
    padding: 24,
    gap: 16,
  },
  formGroup: {
    gap: 4,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  input: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 14,
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none',
    }),
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  inputWithIcon: {
    position: 'relative',
  },
  inputIconLeft: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: [{ translateY: -8 }],
    zIndex: 1,
  },
  inputWithLeftIcon: {
    paddingLeft: 36,
  },
  pickerButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  pickerButtonText: {
    fontSize: 14,
    color: '#111827',
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    overflow: 'hidden',
  },
  picker: {
    height: 40,
  },
  uploadContainer: {
    borderWidth: 2,
    borderColor: '#d1d5db',
    borderStyle: 'dashed',
    borderRadius: 8,
    overflow: 'hidden',
  },
  uploadingContainer: {
    padding: 24,
    alignItems: 'center',
    gap: 8,
  },
  uploadingText: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
  },
  progressBarContainer: {
    width: '100%',
    height: 10,
    backgroundColor: '#e5e7eb',
    borderRadius: 5,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#000000',
  },
  uploadPrompt: {
    padding: 24,
    alignItems: 'center',
    gap: 16,
  },
  uploadText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#000000',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  uploadHint: {
    fontSize: 14,
    color: '#6b7280',
  },
  uploadError: {
    padding: 16,
    backgroundColor: '#fef2f2',
    borderRadius: 6,
  },
  uploadErrorText: {
    fontSize: 14,
    color: '#dc2626',
  },
  uploadedImagesContainer: {
    padding: 24,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    gap: 12,
  },
  uploadedImagesTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  uploadedImagesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  uploadedImageWrapper: {
    position: 'relative',
    width: 100,
    height: 100,
  },
  removeImageButton: {
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: '#ef4444',
    borderRadius: 12,
    padding: 6,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 2,
        elevation: 1,
      },
    }),
  },
  errorText: {
    fontSize: 14,
    color: '#dc2626',
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 12,
    padding: 24,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  cancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  cancelButtonText: {
    fontSize: 14,
    color: '#6b7280',
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  submitButtonDisabled: {
    opacity: 0.5,
    ...(Platform.OS === 'web' && {
      cursor: 'not-allowed',
    }),
  },
  submitButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
});

const pickerStyles = StyleSheet.create({
  pickerModalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  pickerModalBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  pickerModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '60%',
    ...Platform.select({
      web: {
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.1,
        shadowRadius: 10,
        elevation: 5,
      },
    }),
  },
  pickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  pickerHeaderText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  pickerOptions: {
    flex: 1,
  },
  pickerOption: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  pickerOptionSelected: {
    backgroundColor: '#f9fafb',
  },
  pickerOptionText: {
    fontSize: 16,
    color: '#111827',
  },
  pickerOptionTextSelected: {
    fontWeight: '600',
    color: '#000000',
  },
});

export default Inventory;
